
import { useState, useEffect } from 'react';
import { subscriptionService, Subscription } from '@/services/subscription';
import { useAuth } from './useAuth';

export const useSubscription = () => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [isProUser, setIsProUser] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadSubscription();
    } else {
      setSubscription(null);
      setIsProUser(false);
      setLoading(false);
    }
  }, [user]);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      
      // Initialize free subscription if it doesn't exist
      await subscriptionService.initializeFreeSubscription();
      
      // Load current subscription
      const sub = await subscriptionService.getCurrentSubscription();
      setSubscription(sub);
      
      // Check if user has active pro
      const hasActivePro = await subscriptionService.hasActivePro();
      setIsProUser(hasActivePro);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const upgradeToPro = async () => {
    try {
      setLoading(true);
      const updatedSubscription = await subscriptionService.upgradeToPro();
      setSubscription(updatedSubscription);
      setIsProUser(true);
      return { success: true };
    } catch (error) {
      console.error('Error upgrading to pro:', error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  const upgradeToExpert = async () => {
    try {
      setLoading(true);
      const updatedSubscription = await subscriptionService.upgradeToExpert();
      setSubscription(updatedSubscription);
      setIsProUser(true);
      return { success: true };
    } catch (error) {
      console.error('Error upgrading to expert:', error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  return {
    subscription,
    isProUser,
    loading,
    upgradeToPro,
    upgradeToExpert,
    refreshSubscription: loadSubscription
  };
};
